import * as React from "react";
import { EditableTable } from "../../../../../../components/Tables/NewEditableTable";
import { Box, Typography } from "@mui/material";
import { useMIS } from "../MISContext";

const fontSize = "14px";
const cellPadding = "12px";

export default function ManhoursTab() {
  const { manhoursRows, setManhoursRows } = useMIS();

  // Build view rows with per-discipline totals and grand total
  const rowsWithTotals = React.useMemo(() => {
    if (!Array.isArray(manhoursRows) || manhoursRows.length === 0) {
      return [];
    }

    const byDiscipline = new Map();
    (manhoursRows || []).forEach((row) => {
      const discipline = row.discipline || "Other";
      if (!byDiscipline.has(discipline)) byDiscipline.set(discipline, []);
      byDiscipline.get(discipline).push(row);
    });

    const result = [];
    let grandTotal = 0;

    byDiscipline.forEach((rows, discipline) => {
      let disciplineTotal = 0;

      rows.forEach((row, index) => {
        const budget = Number(row.budgetHours) || 0;
        disciplineTotal += budget;
        grandTotal += budget;

        result.push({
          ...row,
          // show discipline label only on first row and use rowSpan to visually "merge" cells
          displayDiscipline: index === 0 ? discipline : "",
          disciplineRowSpan: index === 0 ? rows.length + 1 : 0, // tasks + discipline total row
          hideDisciplineCell: index !== 0,
          rowType: "task",
        });
      });

      result.push({
        id: `${discipline}-total`,
        discipline,
        displayDiscipline: "",
        disciplineRowSpan: 0,
        hideDisciplineCell: true,
        baselineStart: "",
        baselineFinish: "",
        description: `Total ${discipline}`,
        budgetHours: disciplineTotal,
        rowType: "disciplineTotal",
      });
    });

    result.push({
      id: "grand-total",
      discipline: "",
      displayDiscipline: "",
      baselineStart: "",
      baselineFinish: "",
      description: "Total All Disciplines",
      budgetHours: grandTotal,
      rowType: "grandTotal",
    });

    return result;
  }, [manhoursRows]);

  const columns = React.useMemo(
    () => [
      {
        accessorKey: "discipline",
        header: "Discipline",
        enableEditing: false,
        Cell: ({ row }) =>
          row.original.displayDiscipline ?? row.original.discipline ?? "",
        muiTableHeadCellProps: {
          sx: {
            border: "1px solid",
            borderColor: "divider",
            fontSize: "0.95rem",
            fontWeight: "bold",
            padding: cellPadding,
          },
        },
        muiTableBodyCellProps: ({ row }) => ({
          sx: {
            fontSize,
            padding: cellPadding,
            border: "1px solid",
            borderColor: "divider",
            // hide the cell for grouped rows after the first, so the first spans them
            display: row.original.hideDisciplineCell ? "none" : undefined,
            // add background to the visible merged cell using app primary color
            bgcolor: row.original.hideDisciplineCell
              ? undefined
              : "primary.light",
            color: row.original.hideDisciplineCell
              ? undefined
              : "primary.contrastText",
            fontWeight: row.original.hideDisciplineCell ? 400 : 600,
            textAlign: "center",
            verticalAlign: "middle",
          },
          rowSpan: row.original.disciplineRowSpan || 1,
        }),
      },
      {
        accessorKey: "baselineStart",
        header: "Baseline start",
        muiEditTextFieldProps: { type: "date" },
        muiTableHeadCellProps: {
          sx: {
            border: "1px solid",
            borderColor: "divider",
            fontSize: "0.95rem",
            fontWeight: "bold",
            padding: cellPadding,
          },
        },
        muiTableBodyCellProps: {
          sx: {
            fontSize,
            padding: cellPadding,
            border: "1px solid",
            borderColor: "divider",
          },
        },
      },
      {
        accessorKey: "baselineFinish",
        header: "Base line finish",
        muiEditTextFieldProps: { type: "date" },
        muiTableHeadCellProps: {
          sx: {
            border: "1px solid",
            borderColor: "divider",
            fontSize: "0.95rem",
            fontWeight: "bold",
            padding: cellPadding,
          },
        },
        muiTableBodyCellProps: {
          sx: {
            fontSize,
            padding: cellPadding,
            border: "1px solid",
            borderColor: "divider",
          },
        },
      },
      {
        accessorKey: "description",
        header: "Description",
        muiTableHeadCellProps: {
          sx: {
            border: "1px solid",
            borderColor: "divider",
            fontSize: "0.95rem",
            fontWeight: "bold",
            padding: cellPadding,
          },
        },
        muiTableBodyCellProps: {
          sx: {
            fontSize,
            padding: cellPadding,
            border: "1px solid",
            borderColor: "divider",
          },
        },
      },
      {
        accessorKey: "budgetHours",
        header: "Budgeted Hours",
        enableEditing: false, // derived from tasks hours
        Cell: ({ row, cell }) => {
          const value = cell.getValue();
          if (value === "" || value === null || value === undefined) return "";
          const num = Number(value) || 0;
          const isTotal =
            row.original.rowType === "disciplineTotal" ||
            row.original.rowType === "grandTotal";
          return (
            <span style={isTotal ? { fontWeight: 600 } : undefined}>{num}</span>
          );
        },
        muiTableHeadCellProps: {
          sx: {
            border: "1px solid",
            borderColor: "divider",
            fontSize: "0.95rem",
            fontWeight: "bold",
            padding: cellPadding,
          },
        },
        muiTableBodyCellProps: {
          sx: {
            fontSize,
            padding: cellPadding,
            border: "1px solid",
            borderColor: "divider",
          },
        },
      },
    ],
    [],
  );

  const hooks = React.useMemo(
    () => ({
      useCreateItem: () => ({
        mutateAsync: async () => {},
        isPending: false,
      }),
      useGetItems: () => ({
        data: manhoursRows,
        isError: false,
        isFetching: false,
        isLoading: false,
      }),
      useUpdateItem: () => ({
        mutateAsync: async ({ values }) => {
          setManhoursRows((prev) =>
            prev.map((row) =>
              row.id === values.id ? { ...row, ...values } : row,
            ),
          );
        },
        isPending: false,
      }),
      useDeleteItem: () => ({
        mutateAsync: async () => {},
        isPending: false,
      }),
    }),
    [manhoursRows, setManhoursRows],
  );

  return (
    <Box width="100%">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Typography
          variant="h6"
          align="center"
          sx={{
            fontWeight: 600,
            borderRadius: 4,
            px: 3,
            py: 1,
            border: "1px solid",
            borderColor: "divider",
            // backgroundColor: "rgb(255, 228, 225)",
          }}
        >
          Manpower loading
        </Typography>
        <Box sx={{ width: { xs: "100%", md: "60%" } }}>
          <EditableTable
            name="Manpower Loading"
            columns={columns}
            hooks={hooks}
            editMode="cell"
            enableCreate={false}
            enableEditing={false}
            enableDelete={false}
            initialData={{
              discipline: "",
              baselineStart: "",
              baselineFinish: "",
              description: "",
              budgetHours: "",
            }}
            data={rowsWithTotals}
          />
        </Box>
      </Box>
    </Box>
  );
}
